import {
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../auth/services/auth.service';
import { ISwitchableTenant } from '../../../auth/interface/auth.interface';
import { TenantActionService } from '../../../tenant/services/tenant-action.service';
import { IUser } from '../../../user/interfaces/user.interface';
import { environment } from '../../../../environments/environment';
import { MembershipKind } from '../../../shared/enum/membership-kind.enum';
import { UserTypes } from '../../../shared/enum/user-types.enum';

@Component({
    standalone: false,
    selector: 'app-tenant-switcher',
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './tenant-switcher.component.html',
})
export class TenantSwitcherComponent implements OnInit {
    @Input() loggedInUser: IUser | null = null;

    isOpen = false;
    isLoading = false;
    tenants: ISwitchableTenant[] = [];
    ownedCount = 0;
    limit = 0;
    switchingMembershipId: string | null = null;

    constructor(
        private readonly authService: AuthService,
        private readonly tenantActionService: TenantActionService,
        private readonly router: Router,
        private readonly elementRef: ElementRef<HTMLElement>,
    ) {}

    ngOnInit(): void {
        if (this.isTTUserAccount) {
            return;
        }
        void this.load();
    }

    get isTTUserAccount(): boolean {
        return this.loggedInUser?.userType?.key === UserTypes.TT_USER;
    }

    /*=====================================
      Self-signup is closed in production, so the panel offers no way into it.
      The API refuses those routes there as well - this is the door, not the
      lock.
      @return whether opening another store is offered at all
    ======================================*/
    get canOpenAnother(): boolean {
        return !environment.production;
    }

    get atCeiling(): boolean {
        return this.limit > 0 && this.ownedCount >= this.limit;
    }

    /*=====================================
      Only the memberships that are in an actual store. A Seldit membership is
      not a store and has its own way out of one already, in the profile menu -
      so it is dropped here rather than drawn as a row nobody can switch into.

      Reads kind, never tenantId: enterTenantAsTtUser parks a platform
      account's Seldit row ON a store by mutating its tenant_id, so a non-null
      tenant is not proof of a store membership. The API derives kind from the
      membership's user type for exactly that reason.
      @return the tenant-kind memberships
    ======================================*/
    get tenantMemberships(): ISwitchableTenant[] {
        return this.tenants.filter((tenant) => tenant.kind === MembershipKind.TENANT);
    }

    /*=====================================
      There is nothing to switch between until a second store exists, and a
      merchant at their first store should not be shown an empty menu. The
      trigger stays hidden until the list earns it.
      @return whether the switcher is worth drawing
    ======================================*/
    get isWorthShowing(): boolean {
        if (this.isTTUserAccount) {
            return false;
        }

        return this.tenantMemberships.length > 1 || this.ownedCount > 0;
    }

    isCurrent(tenant: ISwitchableTenant): boolean {
        return !!tenant.tenantId && tenant.tenantId === this.loggedInUser?.tenant?.id;
    }

    toggle(): void {
        this.isOpen = !this.isOpen;
    }

    // The menu covers the page, so a click anywhere else closes it - the same
    // thing pressing Escape does.
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
            this.isOpen = false;
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.isOpen = false;
    }

    /*=====================================
      Switches into a store. Already being in it is a no-op rather than a
      round trip that ends where it started.
      @param tenant - the row that was clicked
      @return nothing
    ======================================*/
    onSelect(tenant: ISwitchableTenant): void {
        if (this.isCurrent(tenant) || this.switchingMembershipId) {
            this.isOpen = false;
            return;
        }

        this.switchingMembershipId = tenant.membershipId;
        this.tenantActionService.selectMembership(tenant.membershipId).finally(() => {
            this.switchingMembershipId = null;
            this.isOpen = false;
        });
    }

    /*=====================================
      Opening another store goes to the wizard's own screens - not a copy of
      them inside the panel. There is one wizard in the product.
      @return nothing
    ======================================*/
    onOpenAnother(): void {
        this.isOpen = false;
        void this.router.navigateByUrl(this.atCeiling ? '/onboarding/limit' : '/onboarding/shop');
    }

    private async load(): Promise<void> {
        this.isLoading = true;
        try {
            const { tenants, ownedCount, limit } = await this.authService.getSwitchableTenants();
            this.tenants = tenants;
            this.ownedCount = ownedCount;
            this.limit = limit;
        } finally {
            this.isLoading = false;
        }
    }
}
